import React, { useRef, useState } from "react"
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore/lite"
import { db } from "../../../myFirebase"
import { notifyServiceOrderByEmail } from "../../../services/orderEmailNotifications"
import {
  SERVICE_ORDER_ATTACHMENT_ACCEPT,
  getServiceOrderAttachmentUploadErrorMessage,
  uploadServiceOrderAttachment,
  validateServiceOrderAttachment,
} from "../../../services/serviceOrderAttachment"
import "./index.scss"

const ORDERS_COLLECTION = "serviceOrders"
const CLOSED_ORDER_STATUSES = new Set([
  "traitee",
  "traitée",
  "finalisee",
  "finalisée",
  "terminee",
  "terminée",
  "closed",
  "complete",
  "completed",
  "done",
])

const initialOrderFormState = {
  fullName: "",
  email: "",
  company: "",
  summary: "",
  requestQuote: true,
}

const normalizeStatus = (status) => {
  if (typeof status !== "string") {
    return ""
  }

  return status.trim().toLowerCase()
}

const isOrderClosed = (status) => {
  return CLOSED_ORDER_STATUSES.has(normalizeStatus(status))
}

const hasPendingOrderForService = async ({ email, serviceId }) => {
  const orderQuery = query(
    collection(db, ORDERS_COLLECTION),
    where("email", "==", email),
    where("serviceId", "==", serviceId)
  )

  const querySnapshot = await getDocs(orderQuery)

  return querySnapshot.docs.some((orderDoc) => {
    const orderData = orderDoc.data()
    return !isOrderClosed(orderData.status)
  })
}

const ServiceOrderForm = ({ service, isOpen, onOpen, onClose }) => {
  const fileInputRef = useRef(null)
  const [orderFormData, setOrderFormData] = useState(initialOrderFormState)
  const [attachmentFile, setAttachmentFile] = useState(null)
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false)
  const [orderFeedback, setOrderFeedback] = useState({
    type: "idle",
    message: "",
  })

  const handleOpen = () => {
    onOpen()
    setOrderFeedback({ type: "idle", message: "" })
  }

  const handleClose = () => {
    onClose()
    setOrderFeedback({ type: "idle", message: "" })
  }

  const resetAttachmentField = () => {
    setAttachmentFile(null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleOrderInputChange = (event) => {
    const { name, value, type, checked } = event.target
    setOrderFormData((previousData) => ({
      ...previousData,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleAttachmentChange = (event) => {
    const selectedFile = event.target.files?.[0] || null

    if (!selectedFile) {
      setAttachmentFile(null)
      return
    }

    const attachmentValidationMessage =
      validateServiceOrderAttachment(selectedFile)

    if (attachmentValidationMessage) {
      setOrderFeedback({
        type: "error",
        message: attachmentValidationMessage,
      })
      resetAttachmentField()
      return
    }

    setOrderFeedback({ type: "idle", message: "" })
    setAttachmentFile(selectedFile)
  }

  const validateOrderForm = () => {
    const normalizedSummary = orderFormData.summary.trim()
    const hasAttachment = Boolean(attachmentFile)

    if (!orderFormData.fullName.trim()) {
      return "Veuillez renseigner votre nom complet."
    }

    if (!orderFormData.email.trim()) {
      return "Veuillez renseigner votre adresse email."
    }

    if (!normalizedSummary && !hasAttachment) {
      return "Veuillez renseigner un résumé de votre besoin ou joindre un fichier."
    }

    return ""
  }

  const canSubmitContent =
    orderFormData.summary.trim().length > 0 || Boolean(attachmentFile)

  const submitOrder = async (event) => {
    event.preventDefault()

    const validationMessage = validateOrderForm()
    if (validationMessage) {
      setOrderFeedback({
        type: "error",
        message: validationMessage,
      })
      return
    }

    setIsSubmittingOrder(true)
    setOrderFeedback({ type: "idle", message: "" })

    try {
      const normalizedEmail = orderFormData.email.trim().toLowerCase()
      const normalizedSummary = orderFormData.summary.trim()
      const hasPendingOrder = await hasPendingOrderForService({
        email: normalizedEmail,
        serviceId: service.id,
      })

      if (hasPendingOrder) {
        setOrderFeedback({
          type: "error",
          message:
            "Une demande est déjà en cours pour ce service avec cette adresse email. Vous pourrez en soumettre une nouvelle dès que la précédente sera traitée.",
        })
        return
      }

      let uploadedAttachment = null

      if (attachmentFile) {
        try {
          uploadedAttachment = await uploadServiceOrderAttachment({
            file: attachmentFile,
            serviceId: service.id,
            customerEmail: normalizedEmail,
          })
        } catch (attachmentError) {
          console.error(
            "Service order attachment upload failed",
            attachmentError
          )
          setOrderFeedback({
            type: "error",
            message:
              attachmentError?.message ||
              getServiceOrderAttachmentUploadErrorMessage(attachmentError),
          })
          return
        }
      }

      const orderRef = await addDoc(collection(db, ORDERS_COLLECTION), {
        serviceId: service.id,
        serviceTitle: service.title,
        serviceHighlight: service.highlight,
        fullName: orderFormData.fullName.trim(),
        email: normalizedEmail,
        company: orderFormData.company.trim() || null,
        summary: normalizedSummary || null,
        requestQuote: orderFormData.requestQuote,
        attachment: uploadedAttachment,
        status: "nouvelle",
        source: "services-page",
        createdAt: serverTimestamp(),
      })

      const emailNotificationResult = await notifyServiceOrderByEmail({
        orderId: orderRef.id,
        service,
        customer: {
          fullName: orderFormData.fullName.trim(),
          email: normalizedEmail,
          company: orderFormData.company.trim(),
          summary: normalizedSummary,
          requestQuote: orderFormData.requestQuote,
          attachment: uploadedAttachment,
        },
      })

      const adminConfigured = !emailNotificationResult.missingAdminTemplate
      const clientConfigured = !emailNotificationResult.missingClientTemplate

      if (emailNotificationResult.configurationError) {
        setOrderFeedback({
          type: "error",
          message: `La demande a été enregistrée, mais l'envoi d'email n'est pas configuré correctement (${emailNotificationResult.configurationError}).`,
        })
        setOrderFormData(initialOrderFormState)
        return
      }

      if (emailNotificationResult.clientSent) {
        setOrderFeedback({
          type: "success",
          message:
            "Votre demande a été envoyée avec succès. Un email de confirmation vous a été envoyé, et je reviendrai vers vous dans les plus brefs délais.",
        })
      } else {
        const adminStatusText = emailNotificationResult.adminSent
          ? "Notification admin envoyée."
          : adminConfigured
            ? `Notification admin échouée (${emailNotificationResult.adminError || "erreur inconnue"}).`
            : "Template admin non configuré."

        const clientStatusText = clientConfigured
          ? `Notification client échouée (${emailNotificationResult.clientError || "erreur inconnue"}).`
          : "Template client non configuré."

        setOrderFeedback({
          type: "error",
          message: `La demande a été enregistrée, mais l'email n'a pas pu être envoyé. ${clientStatusText} ${adminStatusText} Vérifiez la configuration EmailJS (To Email, Service ID, Template ID).`,
        })
      }

      setOrderFormData(initialOrderFormState)
      resetAttachmentField()
    } catch (error) {
      setOrderFeedback({
        type: "error",
        message:
          "Une erreur est survenue lors de l'envoi. Veuillez réessayer dans quelques instants.",
      })
    } finally {
      setIsSubmittingOrder(false)
    }
  }

  return (
    <div className="service-order-block">
      <div className="service-card-actions">
        <button
          type="button"
          className="service-order-trigger"
          onClick={isOpen ? handleClose : handleOpen}
          aria-expanded={isOpen}
          aria-controls={`service-order-form-${service.id}`}
        >
          {isOpen ? "Fermer le formulaire" : "Commander ce service"}
        </button>
      </div>

      {isOpen && (
        <form
          id={`service-order-form-${service.id}`}
          className="service-order-form"
          onSubmit={submitOrder}
        >
          <p className="service-order-title">Demander ce service</p>

          <label htmlFor={`fullName-${service.id}`}>Nom complet</label>
          <input
            id={`fullName-${service.id}`}
            type="text"
            name="fullName"
            value={orderFormData.fullName}
            onChange={handleOrderInputChange}
            placeholder="Votre prénom et nom"
            autoComplete="name"
            required
          />

          <label htmlFor={`email-${service.id}`}>Email</label>
          <input
            id={`email-${service.id}`}
            type="email"
            name="email"
            value={orderFormData.email}
            onChange={handleOrderInputChange}
            placeholder="vous@exemple.com"
            autoComplete="email"
            required
          />

          <label htmlFor={`company-${service.id}`}>
            Entreprise (optionnel)
          </label>
          <input
            id={`company-${service.id}`}
            type="text"
            name="company"
            value={orderFormData.company}
            onChange={handleOrderInputChange}
            placeholder="Nom de votre entreprise"
            autoComplete="organization"
          />

          <label htmlFor={`summary-${service.id}`}>
            Résumé de votre besoin
          </label>
          <textarea
            id={`summary-${service.id}`}
            name="summary"
            value={orderFormData.summary}
            onChange={handleOrderInputChange}
            placeholder="Décrivez votre besoin, objectifs, fonctionnalités attendues..."
            rows={4}
          ></textarea>
          <p className="service-order-file-hint">
            Renseignez un résumé ou joignez un document. Les deux sont aussi
            possibles.
          </p>

          <label htmlFor={`attachment-${service.id}`}>
            Pièce jointe (PDF, Word ou Excel - optionnel)
          </label>
          <input
            ref={fileInputRef}
            id={`attachment-${service.id}`}
            type="file"
            name="attachment"
            accept={SERVICE_ORDER_ATTACHMENT_ACCEPT}
            onChange={handleAttachmentChange}
          />
          <p className="service-order-file-hint">
            Taille maximale: 10 Mo. Formats autorisés: PDF, DOC, DOCX, XLS,
            XLSX.
          </p>

          <label
            className="service-order-checkbox"
            htmlFor={`quote-${service.id}`}
          >
            <input
              id={`quote-${service.id}`}
              type="checkbox"
              name="requestQuote"
              checked={orderFormData.requestQuote}
              onChange={handleOrderInputChange}
            />
            Demander un devis
          </label>

          {orderFeedback.message && (
            <p
              className={`service-order-feedback ${
                orderFeedback.type === "error"
                  ? "service-order-feedback-error"
                  : "service-order-feedback-success"
              }`}
              role={orderFeedback.type === "error" ? "alert" : "status"}
              aria-live="polite"
            >
              {orderFeedback.message}
            </p>
          )}

          <div className="service-order-actions">
            <button
              type="button"
              className="service-order-secondary"
              onClick={handleClose}
              disabled={isSubmittingOrder}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="service-order-submit"
              disabled={isSubmittingOrder || !canSubmitContent}
            >
              {isSubmittingOrder ? "Envoi en cours..." : "Envoyer la demande"}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default ServiceOrderForm
